/** Operator copy for bank credits in income verification (applications + workspace). */

export const INCOME_DEPOSIT_LABELS = {
  sectionTitle: 'Bank activity',
  sectionIntro:
    'Credits are deposits used for income analysis. Debits are withdrawals from the same bank feed (context for spending vs pay).',

  outcomeTitle: 'Verification outcome',
  finalVerdict: 'Final verdict',
  calculatedMonthlyIncome: 'Monthly income',
  calculatedMonthlyIncomeHelp:
    'Engine-derived figure from deposit history (recurrence / pay frequency); may differ from the sum of individual credits.',
  primaryPayor: 'Primary payor',

  activityCredits: 'Credits',
  activityDebits: 'Debits',

  tabAll: 'All credits',
  tabUsedForIncome: 'Used for income',
  tabExcluded: 'Excluded',
  tabByPayor: 'By payor',
  tabAllDebits: 'All debits',
  tabInvalidDebits: 'Invalid',
  tabByInstitution: 'By institution',

  colCreditDate: 'Credit date',
  colDebitDate: 'Debit date',
  colAmount: 'Amount',
  colPayor: 'Payor',
  colDescription: 'Description',
  colStatus: 'Status',
  colStatusHelp:
    'Used for income — this deposit was used for the monthly income amount above. Excluded — listed in BankIncomeAudit outlierDetails.excluded only. In analysis only — other eligible credits not used in the monthly total.',
  colDebitStatusHelp:
    'Debit — withdrawal from the bank feed (context only; not used for monthly income). Invalid — flagged during IBV ingest.',
  colExclusionReason: 'Reason',

  statusUsedForIncome: 'Used for income',
  statusExcluded: 'Excluded',
  statusInvalid: 'Invalid',
  statusAnalysisOnly: 'In analysis only',
  statusDebit: 'Debit',

  creditsUsedForIncome: 'Credits used for income',
  creditsExcluded: 'Credits excluded',

  journeyDepositsTitle: 'Bank credits',
  journeyDepositsHint: 'Open a list to review deposits. Use “Used for income” to compare with the monthly income figure.',
  viewUsedForIncome: 'View credits used for income',
  viewExcluded: 'View excluded credits',
  modalOpenHint: 'Opens in a dialog.',
} as const;

/** Which segregated list to show (tabs + journey count clicks). */
export type IncomeDepositViewMode =
  | 'ALL'
  | 'INCLUDED'
  | 'EXCLUDED'
  | 'IN_MONTHLY_INCOME'
  | 'BY_PAYOR'
  | 'INVALID';

export function isDepositIncluded(d: {
  isInvalid: boolean;
  isEligible: boolean;
  excludedByAudit?: boolean;
}): boolean {
  if (d.isInvalid) return false;
  if (typeof d.excludedByAudit === 'boolean') return !d.excludedByAudit;
  return d.isEligible;
}

export function isDepositExcluded(d: {
  isInvalid: boolean;
  isEligible: boolean;
  excludedByAudit?: boolean;
}): boolean {
  if (d.isInvalid) return false;
  if (typeof d.excludedByAudit === 'boolean') return d.excludedByAudit;
  return !d.isEligible;
}

export function depositMatchesView(
  d: {
    isInvalid: boolean;
    isEligible: boolean;
    usedInModel: boolean;
    excludedByAudit?: boolean;
  },
  mode: IncomeDepositViewMode,
): boolean {
  switch (mode) {
    case 'INCLUDED':
      return isDepositIncluded(d);
    case 'EXCLUDED':
      return isDepositExcluded(d);
    case 'IN_MONTHLY_INCOME':
      return d.usedInModel;
    case 'INVALID':
      return d.isInvalid;
    default:
      return true;
  }
}

export function viewModeLabel(mode: IncomeDepositViewMode): string {
  switch (mode) {
    case 'EXCLUDED':
      return INCOME_DEPOSIT_LABELS.tabExcluded;
    case 'IN_MONTHLY_INCOME':
      return INCOME_DEPOSIT_LABELS.tabUsedForIncome;
    case 'BY_PAYOR':
      return INCOME_DEPOSIT_LABELS.tabByPayor;
    case 'INVALID':
      return 'Invalid';
    case 'INCLUDED':
      return 'In analysis only';
    default:
      return INCOME_DEPOSIT_LABELS.tabAll;
  }
}

export type DepositUiStatus = 'feeds_income' | 'excluded' | 'invalid' | 'analysis_only';

export function depositUiStatus(d: {
  isInvalid: boolean;
  isEligible: boolean;
  usedInModel: boolean;
  excludedByAudit?: boolean;
}): DepositUiStatus {
  if (d.isInvalid) return 'invalid';
  if (isDepositExcluded(d)) return 'excluded';
  if (d.usedInModel) return 'feeds_income';
  return 'analysis_only';
}

export function depositStatusLabel(status: DepositUiStatus): string {
  switch (status) {
    case 'feeds_income':
      return INCOME_DEPOSIT_LABELS.statusUsedForIncome;
    case 'excluded':
      return INCOME_DEPOSIT_LABELS.statusExcluded;
    case 'invalid':
      return INCOME_DEPOSIT_LABELS.statusInvalid;
    default:
      return INCOME_DEPOSIT_LABELS.statusAnalysisOnly;
  }
}

export function sumDepositAmounts(
  deposits: { amount: number | null; usedInModel?: boolean }[],
  opts?: { usedInModelOnly?: boolean },
): number {
  let sum = 0;
  for (const d of deposits) {
    if (opts?.usedInModelOnly && !d.usedInModel) continue;
    if (d.amount != null && Number.isFinite(d.amount)) sum += d.amount;
  }
  return sum;
}

/** @deprecated Use IncomeDepositViewMode */
export type IncomeDepositAnalysisFilter = IncomeDepositViewMode;
